import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import { AccountService } from 'app/core';
import { RelatedToType } from '@shared/enums';
import { Account, FieldDef } from 'app/core/models';
import { EditToggle, PanelDef, PanelInterfaceComponent } from './panels.types';
import { PanelFormComponent } from './form/form.component';
import { PanelMatrixComponent } from './matrix/matrix.component';
import { PanelImagesComponent } from './gallery/gallery.component';
import { PanelCommentsComponent } from './comments/comments.component';
import { PanelTableComponent } from './related-table/related-table.component';
import { PanelsActivityComponent } from './activity/activity.component';

@Component({
  selector: 'app-panel-host',
  template: ` <ng-template #panelHost></ng-template>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelHostComponent implements OnInit, OnChanges, PanelInterfaceComponent<any> {
  @Input() panelDef: PanelDef<any>;
  @Input() fieldDefs: FieldDef[];
  @Input() model: any;
  @Input() data: any;
  @Input() readonly = false;
  @Input() canEditLayout = true;
  @Input() account: Account;
  @Input() templateId?: number;
  @Input() relatedToType: RelatedToType;
  @Output() panelDefUpdated = new EventEmitter<PanelDef<any>>();
  @Output() silentPanelDefUpdated = new EventEmitter<PanelDef<any>>();
  @Output() dataUpdated = new EventEmitter<any>();
  @Output() editToggled = new EventEmitter<EditToggle<any>>();

  @ViewChild('panelHost', { static: true, read: ViewContainerRef }) panelHost: ViewContainerRef;

  private panelComponents = {
    activity: PanelsActivityComponent,
    form: PanelFormComponent,
    comments: PanelCommentsComponent,
    table: PanelTableComponent,
    matrix: PanelMatrixComponent,
    images: PanelImagesComponent,
  };

  component: any;

  constructor(private readonly accountService: AccountService) {
    this.account = this.accountService.getCurrentAccount();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model && changes.model.previousValue && changes.model.currentValue) {
      if (this.isUpdated(changes.model.previousValue, changes.model.currentValue)) {
        this.loadComponent();
      }
    }
  }

  ngOnInit(): void {
    this.loadComponent();
  }

  toggleEditMode(): void {
    this.component.instance.toggleEditMode();
  }

  private loadComponent(): void {
    if (this.panelDef.type in this.panelComponents) {
      const viewContainerRef = this.panelHost;
      viewContainerRef.clear();

      const component = this.panelHost.createComponent<PanelInterfaceComponent<any>>(
        this.panelComponents[this.panelDef.type],
      );
      this.component = component;
      component.instance.panelDef = this.panelDef;
      component.instance.fieldDefs = this.fieldDefs;
      component.instance.model = this.model;
      component.instance.data = this.data;
      component.instance.account = this.account;
      component.instance.templateId = this.templateId;
      component.instance.relatedToType = this.relatedToType;
      component.instance.panelDefUpdated = this.panelDefUpdated;
      component.instance.silentPanelDefUpdated = this.silentPanelDefUpdated;
      component.instance.dataUpdated = this.dataUpdated;
      component.instance.editToggled = this.editToggled;
      component.instance.readonly = this.readonly;
      component.instance.canEditLayout = this.canEditLayout;

      if (this.panelDef.type === 'table') {
        component.instance.customObjectsOnly = true;
      }
    }
  }

  private isUpdated(previous: any, current: any): boolean {
    return Object.keys(current).some((key) => previous[key] !== current[key]);
  }
}
